import { Avatar } from '@creditornot/cb-avatar';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { InputRadio } from '@creditornot/cb-radio';
import React, { memo } from 'react';
import { Field, useForm } from 'react-final-form';
import { areEqual } from 'react-window';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography } from 'common/typography';

import { DiscountedItem } from '../../../hooks/useDiscountedItems';
import { CampaignDiscountedItemFormValues, ItemDiscountFormValues } from '../../../types';

export type DiscountedItemListRowRendererProps = {
  data: DiscountedItem[];
  index: number;
  style: React.CSSProperties;
};

const StyledDiscountedItem = styled.label<{ checked?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  height: 60px;
  width: 100%;
  box-shadow: 0 -1px 0 0 ${cssThemedValue('borderPrimary')} inset;
  background-color: ${({ checked }) => (checked ? colors.wolt4 : cssThemedValue('surfaceMain'))};

  &:hover {
    background-color: ${({ checked }) =>
      checked ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const StyledDiscountedItemRadio = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
`;

const StyledDiscountedItemLabel = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const StyledAvatar = styled(Avatar)`
  object-fit: cover;

  img {
    object-fit: cover;
  }
`;

const DiscountedItemListRowRenderer = ({ data, index, style }: DiscountedItemListRowRendererProps) => {
  const { id, baseprice, image, name } = data[index];
  const form = useForm<ItemDiscountFormValues>();

  return (
    <Field<CampaignDiscountedItemFormValues['itemId']> name="itemId" type="radio" value={id}>
      {({ input }) => (
        <StyledDiscountedItem key={id} checked={input.checked} style={style}>
          <StyledDiscountedItemRadio>
            <InputRadio
              {...input}
              onChange={() => {
                input.onChange(id);
                form.batch(() => {
                  form.change('itemTriggerCount', 1);
                  form.change('itemPrice', Number(baseprice.amount));
                  form.change('itemName', name);
                });
              }}
            />
            <StyledDiscountedItemLabel>
              <StyledAvatar size="small" imgSrc={image || undefined} />
              {name}
            </StyledDiscountedItemLabel>
          </StyledDiscountedItemRadio>
          <Typography.Body3 fontWeight="semiBold">
            <MonetaryAmount amount={parseFloat(baseprice.amount)} currency={baseprice.currency} />
          </Typography.Body3>
        </StyledDiscountedItem>
      )}
    </Field>
  );
};

export default memo(DiscountedItemListRowRenderer, areEqual);
